import { useMemo } from 'react';
import { MeiliTrackData } from '../../music24/components/SearchField/types';
import { useTrackDetails } from '../../music24/hooks/useTrackDetails';
import { buildClaimSnapshot } from '../../smartLinks/catalog/buildClaimSnapshot';
import { CatalogClaimSnapshot } from '../../smartLinks/catalog/types';

/** The snapshot of a search hit alone, for a track whose details do not load. */
function hitSnapshot(track: MeiliTrackData, fallbackArtistName: string): CatalogClaimSnapshot {
  return {
    catalogTrackMaid: track.id,
    title: track.name,
    artistName: track.artistName === '' ? fallbackArtistName : track.artistName,
    artworkUrl: track.imageUrl,
  };
}

/**
 * The claim snapshot of the picked search hit. It reads the track by maid, as the Catalog claim
 * step does (`buildClaimSnapshot`), so the claim stores the service links and the release date
 * too. Undefined while the details load; the bare hit when they fail.
 */
export function usePickedSongSnapshot(
  track: MeiliTrackData | undefined,
  fallbackArtistName: string
): CatalogClaimSnapshot | undefined {
  const details = useTrackDetails({ trackId: track?.id ?? '', enabled: track !== undefined });
  const { data, error } = details;
  return useMemo(() => {
    if (track === undefined) {
      return undefined;
    }
    if (data !== undefined) {
      return buildClaimSnapshot(track.id, data, fallbackArtistName);
    }
    return error === null || error === undefined ? undefined : hitSnapshot(track, fallbackArtistName);
  }, [data, error, fallbackArtistName, track]);
}
